import { useEffect, useLayoutEffect, useRef } from 'react';

interface UseHardwareScannerOptions {
  onScan: (value: string) => void;
  enabled?: boolean;
  minLength?: number;
  bufferTimeout?: number;
}

export function useHardwareScanner({
  onScan,
  enabled = true,
  minLength = 3,
  bufferTimeout = 100
}: UseHardwareScannerOptions): void {
  const bufferRef = useRef('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onScanRef = useRef(onScan);
  useLayoutEffect(() => {
    onScanRef.current = onScan;
  });

  useEffect(() => {
    if (!enabled) return;

    function handleKeyDown(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === 'Enter' && bufferRef.current.length >= minLength) {
        e.preventDefault();
        const value = bufferRef.current;
        bufferRef.current = '';
        if (timerRef.current) clearTimeout(timerRef.current);
        onScanRef.current(value);
      } else if (e.key.length === 1) {
        bufferRef.current += e.key;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          bufferRef.current = '';
        }, bufferTimeout);
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [enabled, minLength, bufferTimeout]);
}
